import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { Sparkles, CheckCircle2, Clock, Loader2, ShieldCheck, ClipboardList, ChevronDown, ChevronUp, MessageCircle, Mail, LogOut, KeyRound, Send, Bot, Home, User, Users, LayoutGrid, Building2, Paperclip, X, MapPin, Sun, Moon, SunMoon, PhoneCall, HelpCircle, Cpu, FileText, Download, Smartphone, Megaphone, BarChart3, Pin, TrendingUp, Eye, EyeOff } from "lucide-react";
import PortalTour from "@/components/PortalTour";
import { AddToHomeButton } from "@/components/AddToHomeButton";
import { PropertyValuation } from "@/components/PropertyValuation";
import { formatQuota, DirittoBadge } from "@/components/CatastoPanel";
import { OtpInput } from "@/components/OtpInput";
import { PortalLogin } from "@/pages/portal/PortalLogin";
import { PortalNumeri } from "@/pages/portal/PortalNumeri";
import { PortalAccount } from "@/pages/portal/PortalAccount";
import { PortalImmobile } from "@/pages/portal/PortalImmobile";
import { PortalChat } from "@/pages/portal/PortalChat";
import { PortalHome } from "@/pages/portal/PortalHome";
import { useTheme } from "@/context/ThemeContext";

const BASE = process.env.REACT_APP_BACKEND_URL;
const TKEY = "condomino_token";
// Marker in-memory: sessione autenticata tramite cookie httpOnly (il token vero non è più
// leggibile da JS, quindi non è rubabile via XSS). Il backend legge il cookie, non questo valore.
const SESSION = "__cookie_session__";
const RECENT_MS = 7 * 24 * 3600 * 1000;
const isRecent = (iso) => { try { return iso && (Date.now() - new Date(iso).getTime()) < RECENT_MS; } catch { return false; } };

// Campo password del portale (stile input nativo) con icona mostra/nascondi.
const PwField = ({ className = "", ...props }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input {...props} type={show ? "text" : "password"} className={`${className} pr-10`} />
      <button type="button" tabIndex={-1} onClick={() => setShow((s) => !s)}
        aria-label={show ? "Nascondi password" : "Mostra password"} title={show ? "Nascondi password" : "Mostra password"}
        data-testid={`${props["data-testid"] || "pw"}-toggle`}
        className="absolute right-2.5 top-[21px] -translate-y-1/2 text-muted-foreground hover:text-foreground">
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
};

export default function CondominoPortal() {
  const { token } = useParams();               // presente solo per /c/:token (accesso via link)
  const linkMode = !!token;
  const resetToken = (() => { try { return new URLSearchParams(window.location.search).get("reset_token") || ""; } catch { return ""; } })();
  const otpDemo = (() => { try { return new URLSearchParams(window.location.search).get("otp_demo") === "1"; } catch { return false; } })();
  // Evita lo zoom automatico di iOS Safari sui campi (font-size < 16px) mantenendo la larghezza standard.
  useEffect(() => {
    document.documentElement.classList.add("portal-active");
    return () => document.documentElement.classList.remove("portal-active");
  }, []);

  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [authToken, setAuthToken] = useState(() => localStorage.getItem(TKEY) || "");
  const [u, setU] = useState(""); const [pw, setPw] = useState("");
  const [logErr, setLogErr] = useState(""); const [busy, setBusy] = useState(false);
  const [smsMode, setSmsMode] = useState(false);
  const [smsPhone, setSmsPhone] = useState(""); const [smsStep, setSmsStep] = useState("phone");
  const [smsChallenge, setSmsChallenge] = useState(""); const [smsCode, setSmsCode] = useState("");
  const [smsBusy, setSmsBusy] = useState(false); const [smsErr, setSmsErr] = useState(""); const [smsHint, setSmsHint] = useState("");
  const [smsEntry, setSmsEntry] = useState(0); const [smsResendIn, setSmsResendIn] = useState(0);
  const [smsSuccess, setSmsSuccess] = useState(false);
  const [showPwDlg, setShowPwDlg] = useState(false); const [newPw, setNewPw] = useState("");
  const [oldPw, setOldPw] = useState(""); const [confirmPw, setConfirmPw] = useState(""); const [pwErr, setPwErr] = useState("");
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [segMsg, setSegMsg] = useState("");
  const [segLuogo, setSegLuogo] = useState("");
  const [segFiles, setSegFiles] = useState([]);
  const [segUploading, setSegUploading] = useState(false);
  const [segSending, setSegSending] = useState(false);
  const [segNote, setSegNote] = useState("");
  const [segConsent, setSegConsent] = useState(false);
  const [acceptingPrivacy, setAcceptingPrivacy] = useState(false);
  const [section, setSection] = useState("home");
  const [seenMap, setSeenMap] = useState({});
  const [numeri, setNumeri] = useState(null);
  const [selectedBuilding, setSelectedBuilding] = useState("");
  const [bldMenuOpen, setBldMenuOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotUser, setForgotUser] = useState(""); const [forgotBusy, setForgotBusy] = useState(false); const [forgotSent, setForgotSent] = useState(false);
  const [resetPw, setResetPw] = useState(""); const [resetConfirm, setResetConfirm] = useState("");
  const [resetBusy, setResetBusy] = useState(false); const [resetDone, setResetDone] = useState(false); const [resetErr, setResetErr] = useState("");
  const [visureMine, setVisureMine] = useState([]);
  const [visuraBusy, setVisuraBusy] = useState("");
  const [visuraSession, setVisuraSession] = useState("");
  const [visuraMsg, setVisuraMsg] = useState("");
  const [cfInput, setCfInput] = useState("");
  const [cfBusy, setCfBusy] = useState(false);
  const [cfMsg, setCfMsg] = useState("");
  const [telInput, setTelInput] = useState("");
  const [telBusy, setTelBusy] = useState(false);
  const [telMsg, setTelMsg] = useState("");
  const [notices, setNotices] = useState([]);
  const [polls, setPolls] = useState([]);
  const [homeVal, setHomeVal] = useState(null);
  const [pollChoice, setPollChoice] = useState({});
  const [pollConfirm, setPollConfirm] = useState(null);
  const [pollBusy, setPollBusy] = useState(false);

  const savePhone = async () => {
    const tel = telInput.trim();
    if (tel.replace(/[^\d]/g, "").length < 6) { setTelMsg("Inserisci un numero di telefono valido."); return; }
    setTelBusy(true); setTelMsg("");
    try {
      const r = await fetch(`${BASE}/api/public/condomino/profilo`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ telefono: tel }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Errore");
      setTelInput("");
      setTelMsg("Recapito salvato e sincronizzato con l'amministratore.");
      await loadMe(authToken);
    } catch (e) { setTelMsg(e.message || "Impossibile salvare il telefono."); }
    finally { setTelBusy(false); }
  };

  const saveCf = async () => {
    const cf = cfInput.trim().toUpperCase().replace(/\s+/g, "");
    if (cf.length < 11) { setCfMsg("Inserisci un codice fiscale valido (16 caratteri)."); return; }
    setCfBusy(true); setCfMsg("");
    try {
      const r = await fetch(`${BASE}/api/public/condomino/codice-fiscale`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ codice_fiscale: cf }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Errore");
      setCfInput("");
      setCfMsg("Codice fiscale salvato e sincronizzato con l'amministratore.");
      await loadMe(authToken);
    } catch (e) { setCfMsg(e.message || "Impossibile salvare il codice fiscale."); }
    finally { setCfBusy(false); }
  };

  const loadLink = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/api/public/condomino/link/${token}`);
      if (!r.ok) throw new Error();
      setData(await r.json());
    } catch { setErr(true); } finally { setLoading(false); }
  }, [token]);

  const loadMe = useCallback(async (t) => {
    setLoading(true);
    try {
      // Il cookie httpOnly viene inviato automaticamente (stessa origine). Il Bearer serve solo
      // come fallback/migrazione per chi aveva ancora il token in memoria da localStorage.
      const r = await fetch(`${BASE}/api/public/condomino/me`,
        t && t !== SESSION ? { headers: { Authorization: `Bearer ${t}` } } : {});
      if (!r.ok) throw new Error();
      setData(await r.json());
      setAuthToken((prev) => prev || SESSION); // segna come autenticato (via cookie)
    } catch { setAuthToken(""); setData(null); }
    finally { setLoading(false); }
  }, []);

  const loadVisure = useCallback(async () => {
    if (!authToken) return;
    try {
      const qp = selectedBuilding ? `?building_id=${encodeURIComponent(selectedBuilding)}` : "";
      const r = await fetch(`${BASE}/api/public/condomino/visure${qp}`, { headers: { Authorization: `Bearer ${authToken}` } });
      const j = await r.json();
      setVisureMine(j.visure || []);
    } catch { setVisureMine([]); }
  }, [authToken, selectedBuilding]);

  const buyVisura = async (idImmobile) => {
    if (!selectedBuilding || !idImmobile || visuraBusy) return;
    setVisuraBusy(idImmobile); setVisuraMsg("");
    try {
      const r = await fetch(`${BASE}/api/public/condomino/visura/checkout`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ building_id: selectedBuilding, id_immobile: idImmobile, origin_url: window.location.origin }) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setVisuraMsg(d.detail || "Impossibile avviare il pagamento."); return; }
      const d = await r.json();
      window.location.href = d.checkout_url;
    } catch { setVisuraMsg("Errore di connessione."); }
    finally { setVisuraBusy(""); }
  };

  const downloadVisura = async (sessionId, label) => {
    try {
      const r = await fetch(`${BASE}/api/public/condomino/visura/${sessionId}/document`, { headers: { Authorization: `Bearer ${authToken}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `visura_${(label || "immobile").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { setVisuraMsg("Download non riuscito. Riprova."); }
  };

  // Archivio chat: persiste la conversazione dell'assistente per token (sopravvive ai refresh)
  const chatFirstSave = useRef(true);
  useEffect(() => {
    if (!authToken || !data?.contact) return;
    chatFirstSave.current = true; // salta il primo salvataggio (mount) per non azzerare l'archivio
    try { const saved = localStorage.getItem("condchat_" + data.contact); if (saved) setChatMsgs(JSON.parse(saved)); } catch { /* noop */ }
  }, [authToken, data?.contact]);
  useEffect(() => {
    if (!authToken || !data?.contact) return;
    if (chatFirstSave.current) { chatFirstSave.current = false; return; }
    try { localStorage.setItem("condchat_" + data.contact, JSON.stringify(chatMsgs.slice(-50))); } catch { /* noop */ }
  }, [chatMsgs, authToken, data?.contact]);

  useEffect(() => {
    // Login automatico + ritorno dal pagamento visura (query string)
    const sp = new URLSearchParams(window.location.search);
    const st = sp.get("token");
    const vs = sp.get("visura_session");
    if (st) { setAuthToken(st); }
    if (vs) { setSection("immobile"); setVisuraSession(vs); }
    if (st || vs || sp.get("visura_cancel")) { window.history.replaceState({}, "", "/portal"); }
  }, []);

  // Sicurezza: il token di sessione non viene più conservato in localStorage (rubabile via XSS).
  // Rimuoviamo eventuali token legacy: la sessione ora vive nel cookie httpOnly.
  useEffect(() => { try { localStorage.removeItem(TKEY); } catch { /* noop */ } }, []);

  // Registra il service worker (pass-through) per permettere l'installazione come app.
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => { /* noop */ });
    }
  }, []);

  useEffect(() => {
    if (linkMode) loadLink();
    else loadMe(authToken || undefined);
  }, [linkMode, loadLink, loadMe, authToken]);

  useEffect(() => {
    if (!authToken || !data) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BASE}/api/public/condomino/suggestions`, { headers: { Authorization: `Bearer ${authToken}` } });
        if (!r.ok) return;
        const j = await r.json();
        if (alive) setSuggestions(j.suggestions || []);
      } catch { /* silenzioso */ }
    })();
    return () => { alive = false; };
  }, [authToken, data]);

  // Notifiche "novità": carica lo stato "visto" per condòmino
  useEffect(() => {
    if (!authToken || !data?.contact) { setSeenMap({}); return; }
    try { const s = localStorage.getItem("condseen_" + data.contact); setSeenMap(s ? JSON.parse(s) : {}); }
    catch { setSeenMap({}); }
  }, [authToken, data?.contact]);

  // Quando il condòmino apre/vede la sezione Segnalazioni, le pratiche recenti vengono marcate come viste
  useEffect(() => {
    if (linkMode || section !== "pratiche" || !data || !authToken) return;
    setSeenMap((prev) => {
      const next = { ...prev };
      myPratiche.forEach((p) => { if (isRecent(p.aggiornata_il)) next[p.ref] = p.aggiornata_il; });
      try { if (data?.contact) localStorage.setItem("condseen_" + data.contact, JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, data, linkMode, authToken, selectedBuilding]);

  // Inizializza il condominio selezionato (multi-condominio) quando arrivano i dati
  useEffect(() => {
    const list = data?.condomini || [];
    if (list.length && !list.some((b) => b.id === selectedBuilding)) {
      setSelectedBuilding(list[0].id);
    }
  }, [data, selectedBuilding]);

  // Carica i "Numeri utili" (fornitori abilitati), filtrati per il condominio selezionato
  useEffect(() => {
    if (linkMode || section !== "numeri" || !authToken) return;
    let alive = true;
    (async () => {
      try {
        const qp = selectedBuilding ? `?building_id=${encodeURIComponent(selectedBuilding)}` : "";
        const r = await fetch(`${BASE}/api/public/condomino/numeri-utili${qp}`, { headers: { Authorization: `Bearer ${authToken}` } });
        const j = await r.json();
        if (alive) setNumeri(j.numeri || []);
      } catch { if (alive) setNumeri([]); }
    })();
    return () => { alive = false; };
  }, [section, linkMode, authToken, selectedBuilding]);

  // Carica le visure già acquistate dal condòmino quando apre "Il mio immobile"
  useEffect(() => {
    if (linkMode || section !== "immobile" || !authToken) return;
    loadVisure();
  }, [section, linkMode, authToken, selectedBuilding, loadVisure]);

  // Carica gli avvisi (bacheca) del condominio quando il condòmino è sulla Home
  const loadNotices = useCallback(async () => {
    if (!authToken) return;
    try {
      const qp = selectedBuilding ? `?building_id=${encodeURIComponent(selectedBuilding)}` : "";
      const r = await fetch(`${BASE}/api/public/condomino/notices${qp}`, { headers: { Authorization: `Bearer ${authToken}` } });
      const j = await r.json();
      setNotices(j.notices || []);
    } catch { setNotices([]); }
  }, [authToken, selectedBuilding]);

  useEffect(() => {
    if (linkMode || section !== "home" || !authToken) return;
    loadNotices();
  }, [section, linkMode, authToken, selectedBuilding, loadNotices]);

  const downloadNoticeAtt = async (noticeId, att) => {
    try {
      const r = await fetch(`${BASE}/api/public/condomino/notices/${noticeId}/attachments/${att.id}/download`, { headers: { Authorization: `Bearer ${authToken}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { /* noop */ }
  };

  // Carica i sondaggi del condominio quando il condòmino è sulla Home
  const loadPolls = useCallback(async () => {
    try {
      const qp = selectedBuilding ? `?building_id=${encodeURIComponent(selectedBuilding)}` : "";
      const r = await fetch(`${BASE}/api/public/condomino/polls${qp}`, { headers: { Authorization: `Bearer ${authToken}` } });
      const j = await r.json();
      setPolls(j.polls || []);
    } catch { setPolls([]); }
  }, [authToken, selectedBuilding]);

  useEffect(() => {
    if (linkMode || section !== "home" || !authToken) return;
    loadPolls();
  }, [section, linkMode, authToken, selectedBuilding, loadPolls]);

  // Riepilogo stima del proprio immobile per la card in Home
  useEffect(() => {
    if (linkMode || section !== "home" || !authToken || !data) return;
    const bl = data?.condomini || [];
    const cb = bl.find((b) => b.id === selectedBuilding) || data?.condominio || null;
    const unitId = (cb?.mie_unita || [])[0]?.id_immobile;
    if (!cb?.id || !unitId || !cb?.avm_enabled) { setHomeVal(null); return; }
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BASE}/api/public/condomino/valuation?building_id=${encodeURIComponent(cb.id)}&id_immobile=${encodeURIComponent(unitId)}`, { headers: { Authorization: `Bearer ${authToken}` } });
        const j = await r.json();
        if (alive) setHomeVal(j || null);
      } catch { if (alive) setHomeVal(null); }
    })();
    return () => { alive = false; };
  }, [section, linkMode, authToken, selectedBuilding, data]);

  const doVote = async () => {
    if (!pollConfirm) return;
    const { pollId, optionId } = pollConfirm;
    setPollBusy(true);
    try {
      const r = await fetch(`${BASE}/api/public/condomino/polls/${pollId}/vote`, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ option_id: optionId }),
      });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || "Voto non riuscito"); }
      setPollConfirm(null);
      await loadPolls();
    } catch (e) { setPollConfirm({ ...pollConfirm, error: e.message }); }
    finally { setPollBusy(false); }
  };

  const downloadPollAtt = async (pollId, att) => {
    try {
      const r = await fetch(`${BASE}/api/public/condomino/polls/${pollId}/attachments/${att.id}/download`, { headers: { Authorization: `Bearer ${authToken}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { /* noop */ }
  };

  // Dopo il pagamento: polling della generazione visura, poi ricarica lo storico
  useEffect(() => {
    if (!visuraSession || !authToken) return;
    let alive = true; let tries = 0;
    setVisuraMsg("Pagamento ricevuto, sto generando la visura…");
    const poll = async () => {
      try {
        const r = await fetch(`${BASE}/api/public/condomino/visura/status/${visuraSession}`, { headers: { Authorization: `Bearer ${authToken}` } });
        const j = await r.json();
        if (!alive) return;
        if (j.visura_status === "pronta") {
          setVisuraMsg("Visura pronta! La trovi qui sotto in \"Visure acquistate\".");
          setVisuraSession(""); loadVisure(); return;
        }
        if (j.visura_status === "errore") {
          setVisuraMsg("Errore nella generazione della visura: " + (j.error || "riprova più tardi."));
          setVisuraSession(""); return;
        }
      } catch { /* riprova */ }
      if (alive && tries++ < 20) setTimeout(poll, 3000);
      else if (alive) { setVisuraMsg("La visura sta impiegando più del previsto. Riprova tra poco da \"Visure acquistate\"."); setVisuraSession(""); loadVisure(); }
    };
    poll();
    return () => { alive = false; };
  }, [visuraSession, authToken, loadVisure]);

  // Avvio automatico del tour guidato alla prima visita del portale
  useEffect(() => {
    if (linkMode || !authToken || !data || data.privacy_consent === false) return;
    const key = "cond_tour_seen_v2";
    if (!localStorage.getItem(key)) {
      const t = setTimeout(() => setTourOpen(true), 700);
      return () => clearTimeout(t);
    }
  }, [linkMode, authToken, data]);

  const _selBuilding = (data?.condomini || []).find((b) => b.id === selectedBuilding) || data?.condominio || null;
  const avmEnabled = !!(_selBuilding && _selBuilding.avm_enabled);

  // Se il condominio selezionato non ha la stima immobili (piano non Premium/Elite),
  // non lasciare l'utente su una sezione "immobile" vuota.
  useEffect(() => {
    if (!avmEnabled && section === "immobile") setSection("home");
  }, [avmEnabled, section]);

  const TOUR_STEPS = [
    { title: "Benvenuto nel tuo portale 👋", body: "In pochi passi ti mostro come usarlo. Puoi uscire quando vuoi con la X e rivederlo dal pulsante \"?\" in alto." },
    { selector: "portal-nav-home", action: () => setSection("home"), title: "Home", body: "La panoramica del tuo condominio e le scorciatoie alle funzioni principali. Se sei condòmino in più stabili, in alto puoi scegliere il condominio da visualizzare." },
    { selector: "portal-notices", action: () => setSection("home"), title: "Avvisi dall'amministratore 📣", body: "Qui trovi gli avvisi pubblicati dall'amministratore, con eventuali allegati da scaricare. Quelli \"In evidenza\" (bordo rosso) sono i più importanti e restano in cima. Alcuni avvisi hanno una scadenza e spariscono da soli." },
    { selector: "portal-polls", action: () => setSection("home"), title: "Sondaggi 🗳️", body: "Partecipa alle votazioni del condominio: scegli un'opzione e conferma. Attenzione: puoi votare una sola volta e il voto NON è modificabile. Dopo il voto vedi subito i risultati." },
    { selector: "portal-nav-chat", action: () => setSection("chat"), title: "Amministratore", body: "Scrivi qui per fare domande all'assistente: risponde sulle tue pratiche, sul condominio e sui documenti (regolamento, verbali)." },
    { selector: "portal-pratica-0", action: () => setSection("pratiche"), title: "Segnalazioni", body: "Apri una nuova segnalazione (es. un guasto), allega foto o documenti e segui in tempo reale lo stato. Qui sopra vedi una segnalazione di ESEMPIO con la sua cronologia." },
    ...(avmEnabled ? [{ selector: "portal-nav-immobile", action: () => setSection("immobile"), title: "Il mio immobile", body: "Trovi i dati catastali del tuo immobile e una STIMA automatica del suo valore di mercato, con il confronto con la tua zona. La stima viene calcolata anche quando manca la superficie: puoi inserire i m² reali con \"Migliora la stima\" per un valore più preciso. Puoi anche acquistare la visura catastale in PDF (€8)." }] : []),
    { selector: "numero-card-0", action: () => setSection("numeri"), title: "Numeri utili", body: "I fornitori del tuo condominio che puoi chiamare direttamente, con un tocco sul tasto \"Chiama\". Qui sopra ne vedi uno di ESEMPIO." },
    { selector: "portal-nav-account", action: () => setSection("account"), title: "Account", body: "Gestisci i tuoi dati, aggiungi il numero di cellulare (accesso alternativo) e cambia la password quando vuoi." },
    { title: "Tutto qui! 🎉", body: "Sei pronto a usare il portale. Se ti serve, riavvia questa guida dal pulsante \"?\" in alto a destra." },
  ];

  const closeTour = async () => {
    setTourOpen(false);
    setSection("home");
    // Rimuovi subito i residui demo dalla UI, poi elimina i dati demo dal DB e ricarica i dati reali.
    setNotices([]); setPolls([]);
    try { localStorage.setItem("cond_tour_seen_v2", "1"); } catch { /* noop */ }
    await endPortalTourDemo();
    await Promise.all([loadNotices(), loadPolls()]);
  };

  // Dati DEMO del tour: creati quando il tour si apre (se il condòmino non ha dati reali,
  // le sezioni Segnalazioni e Numeri utili mostrano comunque un esempio), eliminati alla chiusura.
  const startPortalTourDemo = useCallback(async () => {
    if (!authToken) return;
    try {
      await fetch(`${BASE}/api/public/condomino/tour-demo`, { method: "POST", headers: { Authorization: `Bearer ${authToken}` } });
      await loadMe(authToken);
      setNumeri(null);
      await Promise.all([loadNotices(), loadPolls()]);
    } catch (e) { console.debug("portal tour demo:", e); }
  }, [authToken, loadMe, loadNotices, loadPolls]);
  const endPortalTourDemo = useCallback(async () => {
    if (!authToken) return;
    try {
      await fetch(`${BASE}/api/public/condomino/tour-demo`, { method: "DELETE", headers: { Authorization: `Bearer ${authToken}` } });
      await loadMe(authToken);
      setNumeri(null);
    } catch (e) { console.debug("portal tour demo del:", e); }
  }, [authToken, loadMe]);
  const tourDemoRef = useRef(false);
  useEffect(() => {
    if (tourOpen && !tourDemoRef.current) { tourDemoRef.current = true; startPortalTourDemo(); }
    else if (!tourOpen && tourDemoRef.current) { tourDemoRef.current = false; }
  }, [tourOpen, startPortalTourDemo]);



  const login = async () => {
    setBusy(true); setLogErr("");
    try {
      const r = await fetch(`${BASE}/api/public/condomino/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: u.trim(), password: pw }) });
      if (!r.ok) { setLogErr("Credenziali non valide"); return; }
      const d = await r.json();
      setAuthToken(d.token);
    } catch { setLogErr("Errore di connessione"); } finally { setBusy(false); }
  };

  const smsStart = async () => {
    setSmsBusy(true); setSmsErr(""); setSmsSuccess(false);
    // Modalità anteprima animazione (solo frontend, nessuna chiamata reale)
    if (otpDemo) {
      setSmsChallenge("demo"); setSmsHint("••• ••• 233"); setSmsStep("code");
      setSmsCode(""); setSmsEntry(120); setSmsResendIn(30); setSmsBusy(false);
      return;
    }
    try {
      const r = await fetch(`${BASE}/api/public/condomino/sms-otp/start`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: smsPhone.trim() }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setSmsErr(d.detail || "Invio del codice non riuscito."); return; }
      setSmsChallenge(d.challenge_id); setSmsHint(d.phone_hint || ""); setSmsStep("code");
      setSmsCode(""); setSmsEntry(d.entry_window || 120); setSmsResendIn(30);
    } catch { setSmsErr("Errore di connessione"); } finally { setSmsBusy(false); }
  };

  const smsVerify = async (codeArg) => {
    const code = (typeof codeArg === "string" ? codeArg : smsCode).trim();
    if (code.length !== 6) return;
    setSmsBusy(true); setSmsErr("");
    // Modalità anteprima: codice "123456" = successo, qualsiasi altro = errore. Nessun login reale.
    if (otpDemo) {
      if (code === "123456") {
        // pausa di 0,5s con caselle piene, poi animazione (più lenta)
        setTimeout(() => { setSmsSuccess(true); setSmsBusy(false); }, 500);
        setTimeout(() => { setSmsSuccess(false); setSmsCode(""); setSmsErr("✓ Anteprima: codice corretto! (in produzione qui avviene l'accesso)"); }, 4900);
      } else {
        setSmsErr("Anteprima: codice errato. Usa 123456 per vedere l'animazione di successo.");
        setSmsSuccess(false); setSmsCode(""); setSmsBusy(false);
      }
      return;
    }
    try {
      const r = await fetch(`${BASE}/api/public/condomino/otp/verify`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challenge_id: smsChallenge, code, remember_device: true }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setSmsErr(d.detail || "Codice non corretto.");
        setSmsSuccess(false); setSmsCode("");
        setSmsBusy(false);
        return;
      }
      // codice corretto: breve pausa (0,5s), poi animazione (rotazione + conferma), poi entra
      setTimeout(() => { setSmsSuccess(true); setSmsBusy(false); }, 500);
      setTimeout(() => { setAuthToken(d.token); }, 4700);
    } catch { setSmsErr("Errore di connessione"); setSmsBusy(false); }
  };

  useEffect(() => {
    if (!smsMode || smsStep !== "code") return;
    const t = setInterval(() => {
      setSmsEntry((s) => (s > 0 ? s - 1 : 0));
      setSmsResendIn((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [smsMode, smsStep]);

  useEffect(() => { if (otpDemo) setSmsMode(true); }, [otpDemo]);

  const fmtSecs = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const logout = async () => {
    try { await fetch(`${BASE}/api/public/condomino/logout`, { method: "POST", headers: { Authorization: `Bearer ${authToken}` } }); }
    catch (err) { console.error("Logout non riuscito:", err); }
    localStorage.removeItem(TKEY); setAuthToken(""); setData(null);
  };

  const sendForgot = async () => {
    if (!forgotUser.trim()) return;
    setForgotBusy(true);
    try {
      await fetch(`${BASE}/api/public/condomino/forgot-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: forgotUser.trim(), base_url: window.location.origin }) });
      setForgotSent(true);
    } catch { setForgotSent(true); }
    finally { setForgotBusy(false); }
  };

  const submitReset = async () => {
    setResetErr("");
    if (resetPw.trim().length < 6) { setResetErr("La password deve avere almeno 6 caratteri."); return; }
    if (resetPw !== resetConfirm) { setResetErr("Le due password non coincidono."); return; }
    setResetBusy(true);
    try {
      const r = await fetch(`${BASE}/api/public/condomino/reset-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: resetToken, new_password: resetPw.trim() }) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setResetErr(d.detail || "Reset non riuscito."); return; }
      setResetDone(true);
    } catch { setResetErr("Errore di connessione."); }
    finally { setResetBusy(false); }
  };

  const pwScore = (p) => {
    let s = 0;
    if (p.length >= 6) s++;
    if (p.length >= 10) s++;
    if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++;
    if (/\d/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return Math.min(s, 4);
  };
  const PW_LABELS = ["Molto debole", "Debole", "Media", "Buona", "Forte"];
  const PW_COLORS = ["bg-red-500", "bg-red-400", "bg-amber-400", "bg-lime-500", "bg-green-500"];

  const changePw = async () => {
    setPwErr("");
    if (!oldPw) { setPwErr("Inserisci la password attuale."); return; }
    if (newPw.trim().length < 6) { setPwErr("La nuova password deve avere almeno 6 caratteri."); return; }
    if (newPw !== confirmPw) { setPwErr("Le due password non coincidono."); return; }
    try {
      const r = await fetch(`${BASE}/api/public/condomino/change-password`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ old_password: oldPw, new_password: newPw.trim() }) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setPwErr(d.detail || "Cambio password non riuscito."); return; }
      setShowPwDlg(false); setNewPw(""); setOldPw(""); setConfirmPw("");
    } catch { setPwErr("Errore di connessione."); }
  };

  const sendChat = async (preset) => {
    const q = (typeof preset === "string" ? preset : chatInput).trim();
    if (!q || chatBusy) return;
    const history = chatMsgs.map((m) => ({ role: m.role, content: m.content }));
    setChatMsgs((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    setChatInput(""); setChatBusy(true);
    try {
      const r = await fetch(`${BASE}/api/public/condomino/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ question: q, history }),
      });
      if (!r.ok || !r.body) throw new Error();
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const ev = JSON.parse(line.slice(5).trim());
            if (ev.delta) {
              setChatMsgs((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + ev.delta };
                return copy;
              });
            } else if (ev.error) {
              setChatMsgs((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: ev.error };
                return copy;
              });
            }
          } catch { /* noop */ }
        }
      }
    } catch {
      setChatMsgs((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Si è verificato un errore. Riprova." };
        return copy;
      });
    } finally { setChatBusy(false); }
  };

  const uploadSegFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { setSegNote("File troppo grande (max 10 MB)."); e.target.value = ""; return; }
    setSegUploading(true); setSegNote("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await fetch(`${BASE}/api/public/condomino/attachment`, {
        method: "POST", headers: { Authorization: `Bearer ${authToken}` }, body: fd });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setSegFiles((a) => [...a, { file_id: d.file_id, filename: d.filename }]);
    } catch { setSegNote("Caricamento allegato non riuscito. Riprova."); }
    finally { setSegUploading(false); e.target.value = ""; }
  };
  const removeSegFile = (fid) => setSegFiles((a) => a.filter((x) => x.file_id !== fid));

  const sendNewSegnalazione = async () => {
    const message = segMsg.trim();
    const luogo = segLuogo.trim();
    if (!message || !luogo || segSending) return;
    if (!segConsent) { setSegNote("Devi accettare il trattamento dei dati personali per inviare la segnalazione."); return; }
    setSegSending(true); setSegNote("");
    try {
      const r = await fetch(`${BASE}/api/public/condomino/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ message, place: luogo, building_id: selectedBuilding, attachment_ids: segFiles.map((f) => f.file_id), privacy_consent: segConsent }),
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setSegMsg(""); setSegLuogo(""); setSegFiles([]); setSegConsent(false);
      setSegNote(`Segnalazione inviata${d.ref ? ` (pratica ${d.ref})` : ""}. La trovi qui sotto.`);
      if (authToken) loadMe(authToken);
    } catch {
      setSegNote("Invio non riuscito. Riprova più tardi.");
    } finally { setSegSending(false); }
  };

  const acceptPrivacy = async () => {
    setAcceptingPrivacy(true);
    try {
      await fetch(`${BASE}/api/public/condomino/accept-privacy`, {
        method: "POST", headers: { Authorization: `Bearer ${authToken}` },
      });
      if (authToken) loadMe(authToken);
    } finally { setAcceptingPrivacy(false); }
  };

  const { mode: themeMode, setMode: setThemeMode } = useTheme();
  const ThemeToggle = () => (
    <div className="fixed top-4 right-4 z-50 flex items-center gap-0.5 bg-card border border-border rounded-full p-1 shadow-sm" data-testid="portal-theme-toggle">
      {[
        { key: "auto", icon: SunMoon, title: "Automatico" },
        { key: "light", icon: Sun, title: "Chiaro" },
        { key: "dark", icon: Moon, title: "Scuro" },
      ].map((o) => (
        <button key={o.key} data-testid={`portal-theme-${o.key}`} onClick={() => setThemeMode(o.key)} title={o.title} aria-pressed={themeMode === o.key}
          className={`h-7 w-7 flex items-center justify-center rounded-full transition-colors ${themeMode === o.key ? "bg-[#007BFF] text-white" : "text-muted-foreground hover:text-foreground"}`}>
          <o.icon className="h-3.5 w-3.5" />
        </button>
      ))}
    </div>
  );

  const Brand = () => (
    <>
      <ThemeToggle />
      <div className="flex items-center gap-2 mb-8">
        <img src="/sedio-logo.png" alt="Sedio" className="h-8 w-auto object-contain" data-testid="portal-logo" />
        <span className="font-head text-xl font-extrabold tracking-tight">Sedio</span>
      </div>
    </>
  );

  const buildingList = data?.condomini || [];
  const curBuilding = buildingList.find((b) => b.id === selectedBuilding) || data?.condominio || null;
  const mieUnita = curBuilding?.mie_unita || [];
  // Segnalazioni del condominio attualmente selezionato (una persona può avere immobili in più condomìni)
  const myPratiche = (data?.pratiche || []).filter(
    (p) => !p.building_id || !selectedBuilding || p.building_id === selectedBuilding);
  const praticheUpdates = myPratiche.filter(
    (p) => isRecent(p.aggiornata_il) && seenMap[p.ref] !== p.aggiornata_il).length;
  // Segnalazioni non lette per singolo condominio (per il badge nel selettore multi-condominio)
  const unreadForBuilding = (bid) => (data?.pratiche || []).filter(
    (p) => p.building_id === bid && isRecent(p.aggiornata_il) && seenMap[p.ref] !== p.aggiornata_il).length;

  // Reset password via link email (?reset_token=...)
  if (resetToken && !authToken) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center px-4" data-testid="condomino-reset">
        <Brand />
        <div className="w-full max-w-sm bg-card border border-border rounded-2xl p-6">
          <h1 className="font-head text-xl font-extrabold tracking-tight">Reimposta la password</h1>
          {resetDone ? (
            <div className="mt-4" data-testid="reset-done">
              <div className="flex items-center gap-2 text-green-600 text-sm font-semibold"><CheckCircle2 className="h-5 w-5" />Password aggiornata!</div>
              <p className="text-sm text-muted-foreground mt-2">Ora puoi accedere con la nuova password.</p>
              <a href="/portal" className="mt-4 inline-block w-full text-center bg-[#007BFF] hover:bg-[#0069D9] text-white rounded-lg py-2.5 text-sm font-semibold" data-testid="reset-go-login">Vai al login</a>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mt-1 mb-5">Scegli una nuova password per il tuo portale.</p>
              <PwField data-testid="reset-new-pw" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="Nuova password (min 6)" className="w-full mb-2 bg-secondary border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#007BFF]" />
              {resetPw && (
                <div className="mb-2" data-testid="reset-pw-strength">
                  <div className="flex gap-1">{[0, 1, 2, 3].map((i) => (<div key={i} className={`h-1.5 flex-1 rounded-full ${i < pwScore(resetPw) ? PW_COLORS[pwScore(resetPw)] : "bg-secondary border border-border"}`} />))}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">Sicurezza: <span className="font-semibold text-foreground">{PW_LABELS[pwScore(resetPw)]}</span></div>
                </div>
              )}
              <PwField data-testid="reset-confirm-pw" value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)} placeholder="Ripeti la nuova password" className={`w-full mb-2 bg-secondary border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#007BFF] ${resetConfirm && resetConfirm !== resetPw ? "border-red-400" : "border-border"}`} />
              {resetErr && <p className="text-red-400 text-xs mb-2" data-testid="reset-error">{resetErr}</p>}
              <button data-testid="reset-submit" onClick={submitReset} disabled={resetBusy || resetPw.length < 6 || resetPw !== resetConfirm} className="w-full bg-[#007BFF] hover:bg-[#0069D9] text-white rounded-lg py-2.5 text-sm font-semibold flex items-center justify-center disabled:opacity-50">
                {resetBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Imposta nuova password"}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Login (solo modalità /portal senza sessione)
  if (!linkMode && !authToken) {
    return (
      <PortalLogin
        Brand={Brand} PwField={PwField} fmtSecs={fmtSecs} otpDemo={otpDemo}
        u={u} setU={setU} pw={pw} setPw={setPw} logErr={logErr} busy={busy} login={login}
        smsMode={smsMode} setSmsMode={setSmsMode} smsErr={smsErr} setSmsErr={setSmsErr} setLogErr={setLogErr}
        smsStep={smsStep} setSmsStep={setSmsStep} smsPhone={smsPhone} setSmsPhone={setSmsPhone}
        smsBusy={smsBusy} smsHint={smsHint} smsEntry={smsEntry} smsCode={smsCode} setSmsCode={setSmsCode}
        smsSuccess={smsSuccess} smsResendIn={smsResendIn} smsStart={smsStart} smsVerify={smsVerify}
        showForgot={showForgot} setShowForgot={setShowForgot} forgotSent={forgotSent} setForgotSent={setForgotSent}
        forgotUser={forgotUser} setForgotUser={setForgotUser} forgotBusy={forgotBusy} sendForgot={sendForgot}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col items-center py-10 px-4" data-testid="condomino-portal">
      <Brand />
      {data && !linkMode && data.privacy_consent === false && (
        <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="portal-privacy-gate">
          <div className="bg-card border border-border rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="h-5 w-5 text-[#007BFF]" />
              <h2 className="font-head text-lg font-bold">Trattamento dei dati personali</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Per utilizzare il portale devi prendere visione e accettare il trattamento dei tuoi dati personali
              ai sensi del Reg. UE 2016/679, come descritto nell'
              <a href={data?.studio_id ? `/privacy?studio=${encodeURIComponent(data.studio_id)}` : "/privacy"} target="_blank" rel="noopener noreferrer" className="text-[#007BFF] hover:underline">Informativa privacy</a>.
              Il portale utilizza un assistente basato su intelligenza artificiale: consulta l'
              <a href="/legale?doc=ai" target="_blank" rel="noopener noreferrer" className="text-[#007BFF] hover:underline">Informativa AI</a>.
            </p>
            <button data-testid="portal-privacy-accept" onClick={acceptPrivacy} disabled={acceptingPrivacy}
              className="w-full bg-[#007BFF] hover:bg-[#0069D9] text-white rounded-lg px-4 py-2.5 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
              {acceptingPrivacy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}Accetto e continuo
            </button>
          </div>
        </div>
      )}
      {loading ? (
        <Loader2 className="h-6 w-6 animate-spin mt-10 text-muted-foreground" />
      ) : err || !data ? (
        <div className="text-center text-muted-foreground mt-10" data-testid="portal-error">
          <p className="text-lg font-semibold">Link non valido o scaduto</p>
          <p className="text-sm mt-1">Contatta l'amministrazione per assistenza.</p>
        </div>
      ) : (
        <div className="w-full max-w-2xl">
          {/* Barra utente + navigazione (solo portale con login, non link riservato) */}
          {!linkMode && (
            <>
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-muted-foreground">
                  Ciao, <span className="font-semibold text-foreground">{data.contatto}</span>
                </div>
                <div className="flex items-center gap-2">
                  <AddToHomeButton />
                  <button onClick={() => { setSection("home"); setTourOpen(true); }} data-testid="portal-help-tour" className="text-[11px] flex items-center gap-1 text-[#007BFF] hover:underline"><HelpCircle className="h-3.5 w-3.5" />Guida</button>
                  <button onClick={() => setShowPwDlg(true)} data-testid="portal-change-pw" className="text-[11px] flex items-center gap-1 text-muted-foreground hover:text-foreground"><KeyRound className="h-3.5 w-3.5" />Password</button>
                  <button onClick={logout} data-testid="portal-logout" className="text-[11px] flex items-center gap-1 text-muted-foreground hover:text-foreground"><LogOut className="h-3.5 w-3.5" />Esci</button>
                </div>
              </div>
              {/* INTESTAZIONE CONDOMINIO — sempre in alto */}
              {curBuilding && (
                <div className="mb-5 bg-card border border-border rounded-2xl p-4 flex items-start gap-3" data-testid="portal-building-header">
                  <div className="h-11 w-11 rounded-xl bg-[#007BFF]/15 flex items-center justify-center shrink-0"><Building2 className="h-6 w-6 text-[#007BFF]" /></div>
                  <div className="min-w-0">
                    <h2 className="font-head text-lg font-extrabold tracking-tight leading-tight truncate" data-testid="portal-condominio">{curBuilding.nome || "Il tuo condominio"}</h2>
                    {(curBuilding.indirizzo || curBuilding.citta) && (
                      <p className="text-sm text-muted-foreground flex items-start gap-1 mt-0.5">
                        <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                        <span>{[curBuilding.indirizzo, curBuilding.cap, curBuilding.citta, curBuilding.provincia ? `(${curBuilding.provincia})` : ""].filter(Boolean).join(" ")}</span>
                      </p>
                    )}
                    {curBuilding.catasto && (curBuilding.catasto.foglio || curBuilding.catasto.particella) ? (
                      <p className="text-[11px] text-muted-foreground/70 mt-1" data-testid="portal-catasto">
                        Catasto: Fg. {curBuilding.catasto.foglio || "—"} · Part. {curBuilding.catasto.particella || "—"}
                      </p>
                    ) : null}
                    {data.studio && <p className="text-[11px] text-muted-foreground/70 mt-0.5" data-testid="portal-studio">Gestito da {data.studio}</p>}
                  </div>
                </div>
              )}
              {(data.condomini || []).length > 1 && (() => {
                const otherUnread = (data.condomini || []).reduce(
                  (n, b) => n + (b.id !== selectedBuilding ? unreadForBuilding(b.id) : 0), 0);
                return (
                <div className="relative mb-4" data-testid="portal-building-selector-wrap">
                  <button
                    type="button"
                    data-testid="portal-building-selector"
                    onClick={() => setBldMenuOpen((v) => !v)}
                    className="w-full flex items-center gap-2 bg-card border border-border rounded-xl px-3 py-2 hover:border-[#007BFF]/40 transition-colors"
                  >
                    <Building2 className="h-4 w-4 text-[#007BFF] shrink-0" />
                    <span className="text-[11px] text-muted-foreground shrink-0">Condominio:</span>
                    <span className="flex-1 min-w-0 text-left text-sm font-medium text-foreground truncate">{curBuilding?.nome}{curBuilding?.citta ? ` · ${curBuilding.citta}` : ""}</span>
                    {otherUnread > 0 && (
                      <span data-testid="portal-building-other-badge" title="Novità in altri condomìni" className="text-[10px] font-bold bg-[#007BFF] text-white rounded-full px-1.5 py-0.5 shrink-0">{otherUnread}</span>
                    )}
                    <ChevronDown className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${bldMenuOpen ? "rotate-180" : ""}`} />
                  </button>
                  {bldMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setBldMenuOpen(false)} />
                      <div className="absolute left-0 right-0 mt-1 z-20 bg-card border border-border rounded-xl shadow-lg overflow-hidden" data-testid="portal-building-menu">
                        {(data.condomini || []).map((b) => {
                          const cnt = unreadForBuilding(b.id);
                          const active = b.id === selectedBuilding;
                          return (
                            <button
                              key={b.id}
                              type="button"
                              data-testid={`portal-building-option-${b.id}`}
                              onClick={() => { setSelectedBuilding(b.id); setBldMenuOpen(false); }}
                              className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors ${active ? "bg-[#007BFF]/10 font-semibold" : "hover:bg-secondary"}`}
                            >
                              <Building2 className="h-4 w-4 text-[#007BFF] shrink-0" />
                              <span className="flex-1 min-w-0 truncate text-foreground">{b.nome}{b.citta ? ` · ${b.citta}` : ""}</span>
                              {cnt > 0 && (
                                <span data-testid={`portal-building-badge-${b.id}`} title={`${cnt} segnalazioni non lette`} className="text-[10px] font-bold bg-[#007BFF] text-white rounded-full px-1.5 py-0.5 shrink-0">{cnt}</span>
                              )}
                              {active && <CheckCircle2 className="h-4 w-4 text-[#007BFF] shrink-0" />}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
                );
              })()}
              <nav className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 mb-6 bg-card border border-border rounded-2xl p-1.5" data-testid="portal-nav">
                {[
                  { k: "home", label: "Home", icon: Home },
                  { k: "chat", label: "Amministratore", icon: Bot },
                  { k: "pratiche", label: "Segnalazioni", icon: ClipboardList },
                  ...(avmEnabled ? [{ k: "immobile", label: "Il mio immobile", icon: Building2 }] : []),
                  { k: "numeri", label: "Numeri utili", icon: PhoneCall },
                  { k: "account", label: "Account", icon: User },
                ].map((it) => (
                  <button key={it.k} data-testid={`portal-nav-${it.k}`} onClick={() => setSection(it.k)}
                    className={`flex flex-col items-center gap-1 py-2 rounded-xl text-[11px] font-medium transition-colors ${section === it.k ? "bg-[#007BFF] text-white" : "text-muted-foreground hover:bg-secondary"}`}>
                    <it.icon className="h-4 w-4" />{it.label}
                  </button>
                ))}
              </nav>
            </>
          )}

          {/* HOME: riepilogo funzioni del portale — prima cosa che vede il condòmino */}
          {!linkMode && section === "home" && (
            <PortalHome notices={notices} downloadNoticeAtt={downloadNoticeAtt} polls={polls} pollChoice={pollChoice}
              setPollChoice={setPollChoice} setPollConfirm={setPollConfirm} praticheUpdates={praticheUpdates}
              setSection={setSection} myPratiche={myPratiche} avmEnabled={avmEnabled} mieUnita={mieUnita}
              homeVal={homeVal} downloadPollAtt={downloadPollAtt} />
          )}

          {/* SEGNALAZIONI */}
          {(linkMode || section === "pratiche") && (
            <div data-testid="portal-pratiche-section">
              <div className="flex items-center justify-between mb-1">
                <h1 className="font-head text-2xl font-extrabold tracking-tight">Le tue segnalazioni</h1>
                {linkMode && <span className="text-[11px] flex items-center gap-1 text-green-400"><ShieldCheck className="h-3.5 w-3.5" />Riservata</span>}
              </div>
              <p className="text-sm text-muted-foreground mb-6">{data.contatto} · {data.totale} pratic{data.totale === 1 ? "a" : "he"}{data.studio ? ` · Gestito da ${data.studio}` : ""}</p>
              {!linkMode && (
                <div className="bg-card border border-border rounded-2xl p-4 mb-6" data-testid="portal-new-segnalazione">
                  <div className="flex items-center gap-2 mb-3"><Send className="h-4 w-4 text-[#007BFF]" /><h2 className="font-semibold">Invia una nuova segnalazione</h2></div>
                  <label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-1"><MapPin className="h-3 w-3" />Luogo della segnalazione <span className="text-red-400">*</span></label>
                  <input data-testid="seg-luogo" value={segLuogo} onChange={(e) => setSegLuogo(e.target.value)} placeholder="Es. Androne scala B, 2° piano, garage -1…"
                    className="w-full mb-3 bg-secondary border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-[#007BFF]" />
                  <label className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1 block">Descrizione <span className="text-red-400">*</span></label>
                  <textarea data-testid="seg-message" value={segMsg} onChange={(e) => setSegMsg(e.target.value)} rows={4} placeholder="Descrivi il problema (es. perdita d'acqua abbondante, ascensore fermo, ecc.)…"
                    className="w-full resize-none bg-secondary border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-[#007BFF]" />
                  <div className="mt-3">
                    <label className="inline-flex items-center gap-1.5 text-xs border border-dashed border-border rounded-lg px-3 py-2 cursor-pointer hover:border-[#007BFF] transition-colors" data-testid="seg-attach-label">
                      {segUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
                      {segUploading ? "Caricamento…" : "Allega foto o documenti"}
                      <input data-testid="seg-attach-input" type="file" className="hidden" accept=".pdf,.docx,.xlsx,.txt,.png,.jpg,.jpeg,.webp" onChange={uploadSegFile} disabled={segUploading} />
                    </label>
                    {segFiles.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2" data-testid="seg-files">
                        {segFiles.map((f) => (
                          <span key={f.file_id} className="flex items-center gap-1.5 text-xs bg-secondary border border-border rounded-md px-2 py-1">
                            <Paperclip className="h-3 w-3 text-[#007BFF]" />
                            <span className="truncate max-w-[140px]">{f.filename}</span>
                            <button onClick={() => removeSegFile(f.file_id)} className="hover:text-red-400" data-testid={`seg-file-remove-${f.file_id}`}><X className="h-3 w-3" /></button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {segNote && <p className="text-[11px] text-green-400 mt-2" data-testid="seg-note">{segNote}</p>}
                  <label className="flex items-start gap-2 text-[12px] text-muted-foreground cursor-pointer mt-3" data-testid="seg-consent-label">
                    <input type="checkbox" data-testid="seg-consent-checkbox" checked={segConsent}
                      onChange={(e) => setSegConsent(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-border accent-[#007BFF] shrink-0" />
                    <span>Acconsento al trattamento dei miei dati personali ai sensi del Reg. UE 2016/679 (
                      <a href={data?.studio_id ? `/privacy?studio=${encodeURIComponent(data.studio_id)}` : "/privacy"} target="_blank" rel="noopener noreferrer" className="text-[#007BFF] hover:underline">Informativa privacy</a>).
                    </span>
                  </label>
                  <div className="flex justify-end mt-3">
                    <button data-testid="seg-send" onClick={sendNewSegnalazione} disabled={segSending || segUploading || !segMsg.trim() || !segLuogo.trim() || !segConsent}
                      className="bg-[#007BFF] hover:bg-[#0069D9] text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50 flex items-center gap-2">
                      {segSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Invia segnalazione
                    </button>
                  </div>
                </div>
              )}
              {myPratiche.length === 0 ? (
                <div className="text-center text-muted-foreground mt-10 text-sm">Nessuna segnalazione registrata per questo condominio.</div>
              ) : (
                <div className="space-y-3">
                  {myPratiche.map((p, i) => (
                    <div key={p.ref || i} className="bg-card border border-border rounded-2xl p-4 backdrop-blur" data-testid={`portal-pratica-${i}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] font-mono text-muted-foreground">{p.ref}</span>
                        <span className="text-[10px] text-muted-foreground/70 flex items-center gap-1">
                          {p.canale === "whatsapp" ? <MessageCircle className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
                          {(p.creata_il || "").slice(0, 10)}
                        </span>
                      </div>
                      <h2 className="font-semibold mt-1.5 flex items-start gap-2"><ClipboardList className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />{p.oggetto}</h2>
                      {p.condominio && <p className="text-xs text-muted-foreground mt-1 ml-6">{p.condominio}</p>}
                      <div className="mt-3 ml-6 flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${p.chiusa ? "bg-green-500/20 text-green-400" : "bg-[#007BFF]/20 text-[#007BFF]"}`} data-testid={`portal-status-${i}`}>
                          {p.chiusa ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}{p.stato}
                        </span>
                        {(p.timeline || []).length > 0 && (
                          <button onClick={() => setOpen(open === i ? null : i)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-0.5">
                            Dettagli {open === i ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                          </button>
                        )}
                      </div>
                      {open === i && (
                        <ol className="relative border-l border-border ml-8 mt-4 space-y-3">
                          {p.timeline.map((t, k) => (
                            <li key={k} className="ml-4">
                              <span className="absolute -left-[5px] h-2.5 w-2.5 rounded-full bg-[#007BFF]" />
                              <div className="text-sm">{t.label}</div>
                              <div className="text-[10px] text-muted-foreground/70">{(t.ts || "").replace("T", " ").slice(0, 16)}</div>
                            </li>
                          ))}
                        </ol>
                      )}
                      <p className="text-[10px] text-muted-foreground/70 mt-3 ml-6">Ultimo aggiornamento: {(p.aggiornata_il || "").replace("T", " ").slice(0, 16)}</p>
                    </div>
                  ))}
                </div>
              )}
              {linkMode && <p className="text-[11px] text-muted-foreground/70 mt-8 text-center">Pagina di sola consultazione. Per aggiornamenti rispondi al canale con cui hai inviato la segnalazione.</p>}
            </div>
          )}

          {/* ACCOUNT */}
          {!linkMode && section === "account" && (
            <PortalAccount data={data} telInput={telInput} setTelInput={setTelInput} savePhone={savePhone}
              telBusy={telBusy} telMsg={telMsg} setShowPwDlg={setShowPwDlg} logout={logout} />
          )}

          {/* IL MIO IMMOBILE — dati catastali + visura a pagamento del proprio immobile */}
          {!linkMode && section === "immobile" && avmEnabled && (
            <PortalImmobile data={data} curBuilding={curBuilding} cfInput={cfInput} setCfInput={setCfInput}
              saveCf={saveCf} cfBusy={cfBusy} cfMsg={cfMsg} visuraMsg={visuraMsg} visureMine={visureMine}
              downloadVisura={downloadVisura} mieUnita={mieUnita} visuraBusy={visuraBusy} buyVisura={buyVisura}
              authToken={authToken} />
          )}

          {/* NUMERI UTILI — fornitori che il condòmino può chiamare in autonomia */}
          {!linkMode && section === "numeri" && (
            <PortalNumeri numeri={numeri} />
          )}

          {/* ASSISTENTE AI — sezione a parte */}
          {!linkMode && section === "chat" && (
            <PortalChat chatMsgs={chatMsgs} suggestions={suggestions} chatBusy={chatBusy}
              sendChat={sendChat} chatInput={chatInput} setChatInput={setChatInput} />
          )}
        </div>
      )}

      {showPwDlg && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4" onClick={() => { setShowPwDlg(false); setPwErr(""); }}>
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="portal-pw-dialog">
            <h3 className="font-semibold mb-4">Cambia password</h3>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1 block">Password attuale</label>
            <PwField data-testid="portal-old-pw" value={oldPw} onChange={(e) => setOldPw(e.target.value)} placeholder="Password attuale" className="w-full mb-3 bg-secondary border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#007BFF]" />
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1 block">Nuova password</label>
            <PwField data-testid="portal-new-pw" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Nuova password (min 6)" className="w-full mb-2 bg-secondary border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#007BFF]" />
            {newPw && (
              <div className="mb-3" data-testid="pw-strength">
                <div className="flex gap-1">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className={`h-1.5 flex-1 rounded-full ${i < pwScore(newPw) ? PW_COLORS[pwScore(newPw)] : "bg-secondary border border-border"}`} />
                  ))}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">Sicurezza: <span className="font-semibold text-foreground">{PW_LABELS[pwScore(newPw)]}</span></div>
              </div>
            )}
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1 block">Conferma nuova password</label>
            <PwField data-testid="portal-confirm-pw" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder="Ripeti la nuova password" className={`w-full mb-2 bg-secondary border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#007BFF] ${confirmPw && confirmPw !== newPw ? "border-red-400" : "border-border"}`} />
            {pwErr && <p className="text-red-400 text-xs mb-3" data-testid="portal-pw-error">{pwErr}</p>}
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => { setShowPwDlg(false); setPwErr(""); }} className="text-sm px-3 py-2 rounded-lg text-muted-foreground hover:text-foreground">Annulla</button>
              <button data-testid="portal-save-pw" onClick={changePw} disabled={!oldPw || newPw.trim().length < 6 || newPw !== confirmPw} className="text-sm px-4 py-2 rounded-lg bg-[#007BFF] hover:bg-[#0069D9] text-white font-semibold disabled:opacity-50">Salva</button>
            </div>
          </div>
        </div>
      )}

      {pollConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4" onClick={() => !pollBusy && setPollConfirm(null)}>
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="portal-poll-confirm">
            <h3 className="font-semibold mb-2 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-[#007BFF]" />Confermi il voto?</h3>
            <p className="text-sm text-muted-foreground mb-3">Stai per votare al sondaggio <b className="text-foreground">"{pollConfirm.title}"</b>.</p>
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 mb-4">
              <ShieldCheck className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
              <span className="text-[13px] text-amber-800">Attenzione: una volta confermato, <b>il voto non è modificabile</b> e potrai votare una sola volta.</span>
            </div>
            {pollConfirm.error && <p className="text-red-500 text-xs mb-3" data-testid="portal-poll-error">{pollConfirm.error}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={() => setPollConfirm(null)} disabled={pollBusy} className="text-sm px-3 py-2 rounded-lg text-muted-foreground hover:text-foreground">Annulla</button>
              <button data-testid="portal-poll-confirm-btn" onClick={doVote} disabled={pollBusy} className="text-sm px-4 py-2 rounded-lg bg-[#007BFF] hover:bg-[#0069D9] text-white font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
                {pollBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Conferma voto
              </button>
            </div>
          </div>
        </div>
      )}


      {tourOpen && <PortalTour steps={TOUR_STEPS} onClose={closeTour} />}
    </div>
  );
}
